/**
 * GSMA SGP.22 activation-code scheme. A device expects the full string
 * `LPA:1$<SM-DP+ address>$<matching id>` when the code is typed or scanned.
 */
const LPA_SCHEME = "LPA:1$";

type ParsedEsimActivationCode = {
  smdpPlusAddress: string;
  activationCode: string;
  completeActivationCode: string;
};

/**
 * Normalises a SIM `matchingId` from the API into the three parts shown in
 * the eSIM profile. The backend has been seen to send:
 * - the GSMA string (`LPA:1$connect.com$…`)
 * - a Compax-style prefix (`matching_id:connect.com$…`)
 * - just `address$code`
 *
 * In every case the complete code we display and encode in the QR is
 * `LPA:1$<address>$<code>`.
 */
export const parseEsimMatchingId = (
  matchingId: string,
): ParsedEsimActivationCode | undefined => {
  const remainder = matchingId
    .trim()
    .replace(/^matching_id:/, "")
    .replace(/^LPA:1\$/i, "");
  const [smdpPlusAddress, activationCode, ...rest] = remainder.split("$");

  if (!smdpPlusAddress || !activationCode) {
    return undefined;
  }

  const extra = rest.length > 0 ? `$${rest.join("$")}` : "";

  return {
    smdpPlusAddress,
    activationCode,
    completeActivationCode: `${LPA_SCHEME}${smdpPlusAddress}$${activationCode}${extra}`,
  };
};

/**
 * The value to encode in an eSIM activation QR code: the complete
 * `LPA:1$…` string, or `undefined` when the `matchingId` is missing or
 * malformed.
 *
 * Callers must NOT fall back to the raw `matchingId` - a QR built from an
 * unparseable code encodes something no device can install, so an invalid
 * code has to surface as an error hint rather than a dead QR.
 */
export const getEsimQrValue = (
  matchingId: string | undefined,
): string | undefined =>
  matchingId
    ? parseEsimMatchingId(matchingId)?.completeActivationCode
    : undefined;
